import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Zap, FileText, HelpCircle, AlertTriangle } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const FACTS = [
  ["1er janvier 2026", "La facturation électronique structurée devient obligatoire entre assujettis TVA belges (B2B)."],
  ["Format Peppol BIS", "Un PDF envoyé par email ne suffit plus : la facture doit transiter par le réseau Peppol au format UBL."],
  ["Toutes les tailles", "Indépendants, SRL, SPRL : tous les artisans assujettis sont concernés, quel que soit le chiffre d'affaires."],
  ["Sanctions progressives", "Amendes de 1 500 € à 5 000 € en cas de non-conformité répétée après avertissement."],
];

const STEPS = [
  ["Créez votre compte", "Deux minutes, sans carte bancaire. Votre numéro de TVA est vérifié automatiquement."],
  ["Faites votre devis ou facture", "Dictez le chantier, Devisto chiffre les lignes. Le client signe en ligne."],
  ["Envoyez via Peppol", "Un clic : la facture part au format officiel. Vous suivez la livraison (reçue, en attente, échec)."],
];

const FAQ = [
  ["Dois-je ouvrir un compte Peppol moi-même ?", "Non. Devisto est connecté à un point d'accès Peppol certifié : vous envoyez depuis l'application, nous nous occupons du reste."],
  ["Et mes clients particuliers ?", "L'obligation ne concerne que les factures entre professionnels (B2B). Pour les particuliers, Devisto envoie toujours un PDF par email, et le client paie en ligne."],
  ["Mon comptable peut-il récupérer mes factures ?", "Oui : export CSV comptable en un clic, et format UBL standard accepté par les logiciels de comptabilité belges."],
  ["Que se passe-t-il si le destinataire n'est pas sur Peppol ?", "Devisto détecte l'absence du destinataire, vous prévient et bascule automatiquement sur l'envoi par email, sans rien perdre."],
  ["Combien ça coûte ?", "Devisto Studio est à 25 € HTVA par mois, Peppol inclus, sans limite de factures. Essai gratuit de 30 jours."],
];

export default function PeppolLanding() {
  useEffect(() => {
    document.title = "Facture Peppol obligatoire 2026 en Belgique — guide artisan | Devisto";
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute("content", "Facturation électronique Peppol obligatoire en Belgique depuis le 1er janvier 2026 : ce que les artisans et indépendants du bâtiment doivent faire, et comment Devisto l'automatise pour 25 €/mois.");
    return () => { if (meta && prev) meta.setAttribute("content", prev); };
  }, []);

  return (
    <div className="font-body min-h-screen bg-[#F3F5FC] text-[#0F2645]">
      <Header />
      <main data-testid="peppol-landing">
        <section className="relative overflow-hidden" style={{ background: "linear-gradient(168deg, #163663 0%, #0F2645 55%, #0A1B33 100%)" }}>
          <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(110% 80% at 80% 6%, rgba(58,92,200,0.26) 0%, rgba(58,92,200,0) 60%)" }} />
          <div className="relative mx-auto max-w-5xl px-5 py-16 lg:py-24">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-400/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200">
              <AlertTriangle className="h-3.5 w-3.5" /> Obligation légale depuis le 1er janvier 2026
            </span>
            <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Facture Peppol obligatoire : <span className="text-[#9FB4F2]">êtes-vous en règle ?</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#C8D2EC] md:text-lg">
              Depuis 2026, chaque facture entre professionnels en Belgique doit passer par le réseau Peppol. Devisto l'envoie pour vous, au bon format, directement depuis votre devis signé.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/studio/inscription" data-testid="peppol-cta-primary" className="inline-flex items-center gap-2 rounded-xl bg-[#3A5CC8] px-7 py-4 text-sm font-bold text-white shadow-[0_6px_16px_-8px_rgba(58,92,200,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#3B5ECB]">
                Me mettre en règle gratuitement <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#comment" className="inline-flex items-center gap-2 rounded-xl border border-white/25 px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-white/10">Comment ça marche</a>
            </div>
            <p className="mt-4 text-xs text-[#9FB4F2]">30 jours gratuits · sans carte · Peppol inclus · 25 € HTVA/mois ensuite</p>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-heading text-base font-bold uppercase tracking-[0.14em] text-[#3A5CC8] md:text-lg">Ce que dit la loi</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {FACTS.map(([t, d], i) => (
              <div key={t} className="dv-card-in rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]" style={{ animationDelay: `${i * 60}ms` }}>
                <p className="font-heading text-lg font-extrabold text-[#0F2645]">{t}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-[#5C6A87]">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-[#93A0BC]">Source : SPF Finances — loi du 6 février 2024 relative à la facturation électronique B2B. Informations indicatives, vérifiez auprès de votre comptable.</p>
        </section>

        <section id="comment" className="bg-white py-14">
          <div className="mx-auto max-w-5xl px-5">
            <h2 className="font-heading text-base font-bold uppercase tracking-[0.14em] text-[#3A5CC8] md:text-lg">Comment Devisto vous met en règle</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              {STEPS.map(([t, d], i) => (
                <div key={t} className="relative rounded-2xl border border-[#E2E7F7] bg-[#F6F8FD] p-5">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0F2645] font-mono text-sm font-bold text-white">{i + 1}</span>
                  <p className="mt-3 font-heading text-base font-bold text-[#0F2645]">{t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#5C6A87]">{d}</p>
                </div>
              ))}
            </div>
            <ul className="mt-8 grid gap-2 sm:grid-cols-2">
              {["Numéro de TVA vérifié à l'inscription", "Format Peppol BIS 3.0 généré automatiquement", "Suivi de livraison : reçue, en attente, échec", "Relance email automatique si Peppol échoue", "Devis signé → facture en un clic", "Export comptable CSV + UBL"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-[#0F2645]"><CheckCircle2 className="h-4 w-4 shrink-0 text-[#3A5CC8]" /> {x}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-heading text-base font-bold uppercase tracking-[0.14em] text-[#3A5CC8] md:text-lg">Questions fréquentes</h2>
          <div className="mt-6 space-y-3">
            {FAQ.map(([q, a]) => (
              <details key={q} data-testid="peppol-faq-item" className="group rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
                <summary className="flex cursor-pointer list-none items-center gap-3 font-heading text-base font-bold text-[#0F2645]">
                  <HelpCircle className="h-4 w-4 shrink-0 text-[#3A5CC8]" /> {q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-[#5C6A87]">{a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="dv-navy mx-auto mb-16 max-w-5xl rounded-3xl px-6 py-12 text-center text-white lg:px-12">
          <ShieldCheck className="mx-auto h-10 w-10 text-[#9FB4F2]" />
          <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Passez à la facture Peppol en 2 minutes</h2>
          <p className="mt-3 text-sm text-[#C8D2EC] md:text-base">Devis dicté, signature en ligne, facture Peppol : tout le travail du bâtiment, du chantier à la facture.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/studio/inscription" data-testid="peppol-cta-bottom" className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 text-sm font-bold text-[#0F2645] transition-all hover:-translate-y-0.5"><Zap className="h-4 w-4 text-[#3A5CC8]" /> Essayer gratuitement</Link>
            <Link to="/" className="inline-flex items-center gap-2 rounded-xl border border-white/25 px-7 py-4 text-sm font-semibold text-white hover:bg-white/10"><FileText className="h-4 w-4" /> Découvrir Devisto</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
